import AddIcon from '@mui/icons-material/Add'
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined'
import { Box } from '@mui/material'
import type { GridFilterModel, GridPaginationModel } from '@mui/x-data-grid-pro'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import ButtonBar from '@/components/inputs/ButtonBar'
import CustomButton from '@/components/inputs/CustomButton'
import PageHeader from '@/components/layouts/PageHeader'
import { DEFAULT_DATA_GRID_PAGINATION_MODEL } from '@/constants/datagrid'
import { useDataGridSyncUrlManager } from '@/contexts/DataGridSyncUrlManagerContext'
import { useActivationGroupsQuery } from '@/features/activationGroup/hooks/useActivationGroupsQuery'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import BiddingActionsMenu from '@/features/bidding/components/BiddingActionsMenu'
import CountrySelector from '@/features/bidding/components/CountrySelector'
import BidsOverviewSummaryDataGrid from '@/features/bidding/components/overview/BidsOverviewSummaryDataGrid'
import { DEFAULT_DATA_GRID_BIDS_FILTER_MODEL } from '@/features/bidding/components/todoList/BiddingTodoListFilterToolbar'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { usePortfoliosQuery } from '@/features/bidding/hooks/usePortfoliosQuery'
import { useFeatureToggle } from '@/features/featureToggle/contexts/FeatureToggleContext'

const BiddingOverviewSummary = () => {
  const { t } = useTranslation()
  const { isEnabled } = useFeatureToggle()
  const { permissions } = usePermissions()
  const canMutateBidding = permissions.has('biddingMutation')
  const { selectedCountry } = useBiddingContext()
  const { activationGroups } = useActivationGroupsQuery({ countryCode: selectedCountry })
  const { portfolios } = usePortfoliosQuery({ countryCode: selectedCountry })
  const syncUrlManager = useDataGridSyncUrlManager()
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    syncUrlManager.initialState.paginationModel ?? DEFAULT_DATA_GRID_PAGINATION_MODEL,
  )
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    syncUrlManager.initialState.filterModel ?? DEFAULT_DATA_GRID_BIDS_FILTER_MODEL,
  )

  function handleSelectedCountryChange(countryCode: CountryIdentifier) {
    setPaginationModel({
      ...paginationModel,
      page: 0,
    })
    setFilterModel(DEFAULT_DATA_GRID_BIDS_FILTER_MODEL)
    syncUrlManager.syncGlobalFilterSearchParams({ countryCode })
  }

  return (
    <>
      <PageHeader
        isRoot
        appBarContent={
          <Box alignItems="center" display="flex" flexDirection="row" sx={{ width: '100%' }}>
            <Box display="flex" flexDirection="row" sx={{ flex: 1, justifyContent: 'left' }}>
              <CountrySelector onChange={handleSelectedCountryChange} />
            </Box>
            <ButtonBar alignment="right" sx={{ flex: 1 }}>
              {selectedCountry === 'fi' && isEnabled('FLEX_PORTAL_ENABLE_IMPORT_ENERGY_OFFERS') ? (
                <BiddingActionsMenu canMutateBidding={canMutateBidding} />
              ) : (
                <CustomButton
                  color="inheritWhite"
                  component={Link}
                  startIcon={<GetAppOutlinedIcon />}
                  to="/bidding/exports"
                  variant="outlined"
                >
                  {t(`bidding.exports.title`)}
                </CustomButton>
              )}
              {canMutateBidding && (
                <CustomButton
                  color="inheritText"
                  component={Link}
                  // Remove once bid creation is enabled for Greece
                  disabled={selectedCountry === 'gr'}
                  startIcon={<AddIcon />}
                  sx={{ color: 'black' }}
                  to="/bidding/todo-list"
                  variant="contained"
                >
                  {t(`bidding.overview.add_new_bid`)}
                </CustomButton>
              )}
            </ButtonBar>
          </Box>
        }
        pageTitle={t('component.page_header.bidding')}
      />

      <BidsOverviewSummaryDataGrid
        activationGroups={activationGroups}
        filterModel={filterModel}
        paginationModel={paginationModel}
        portfolios={portfolios}
        selectedCountry={selectedCountry}
        setFilterModel={setFilterModel}
        setPaginationModel={setPaginationModel}
      />
    </>
  )
}

export default BiddingOverviewSummary
